import { openWorkOrder } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from '@mui/material/Grid2';
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { useEffect, useMemo, useState } from "react";
import { useWorkOrderModalsContext } from "../context/work-order-modals.context";
import type { ModalWorkOrder } from "../models/ModalWorkOrder";
import type { WorkOrder } from "@/pages/work-orders/models/WorkOrder";
import { ErrorModal, getMomentTz, HelmetDexteel } from "@dexteel/mesf-core";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	data: ModalWorkOrder;
};

export function OpenWorkOrderModal({ show, onHide, data }: Props) {
	const [openSuccess, setOpenSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const {
		state: {
			modalWorkOrderSelected: {
				TemplateTitle,
				WorkOrderPlannedHours,
				WorkOrderNumberOfWorkers,
				WorkOrderOpenedBy,
				OpenTimestamp,
				WorkOrderName,
				Title,
				AssetName,
				ConsumedHours,
			},
		},
		actions: { setAssetName },
	} = useWorkOrderModalsContext();

	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");
	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});

	const { state, actions } = useWorkOrderModalsContext();

	const [error, setError] = useState<string>("");

	//functions
	const handleClose = (event: Event | React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSuccess(false);
	};

	const handleCancel = () => {
		onHide(false);
	};

	const handleOpenWorkOrder = async () => {
		setIsSubmitLoading(true);
		const res = await openWorkOrder(state.modalWorkOrderSelected as WorkOrder);
		if (res.ok) {
			setIsSubmitLoading(false);
			setOpenSuccess(true);
			onHide(true);
		} else {
			setError(res.message);
			setIsSubmitLoading(false);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			actions.setComments(data.Comments);
			actions.setTemplateTitle(data.TemplateTitle);
			actions.setWorkOrderPlannedHours(data.WorkOrderPlannedHours);
			actions.setWorkOrderNumberOfWorkers(data.WorkOrderNumberOfWorkers);
			actions.setWorkOrderOpenedBy(data.WorkOrderOpenedBy);
			actions.setOpenTimestamp(data.OpenTimestamp);
			actions.setWorkOrderName(data.WorkOrderName);
			actions.setTitle(data.Title);
			GetAssetFullPath((data.AssetId as number).toString());
			actions.setWorkOrderId(data.WorkOrderId);
			actions.setWorkOrderId(data.WorkOrderId);
			actions.setWorkOrderConsumedHours(data.ConsumedHours);
		}
		setIsSubmitLoading(false);
	}, [show]);

	const formatDescription = (description: string): string => {
		if (description?.includes("/") && description.split("/").length > 3) {
			const parts = description.split("/");
			let formattedDescription = "";

			for (let i = 0; i < parts.length; i++) {
				if (i < parts.length - 2) {
					formattedDescription += ".../";
				} else if (i === parts.length - 2) {
					if (parts[i].length > 6) {
						formattedDescription += parts[i].substring(0, 6) + ".../";
					} else {
						formattedDescription += parts[i] + "/";
					}
				} else {
					formattedDescription += parts[i];
					if (i < parts.length - 1) {
						formattedDescription += "/";
					}
				}
			}

			return formattedDescription;
		} else {
			return description;
		}
	};

	const description = useMemo(
		() => formatDescription(AssetName as string),
		[AssetName],
	);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Open Work Order - ${WorkOrderName || "Order"}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<DialogTitle
					sx={{
						padding: "15px 24px 10px 24px",
					}}
				>
					<Grid2 sx={{ fontSize: "1.7em", fontWeight: "bold" }}>
						OPEN: {WorkOrderName}
					</Grid2>
					{TemplateTitle && <Grid2>{data?.TemplateTitle}</Grid2>}
				</DialogTitle>
				<DialogContent dividers sx={{ paddingTop: "0px", overflow: "hidden" }}>
					<Grid2 container spacing={2} sx={{ margin: 0, width: "100%" }}>
						<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 6, xs: 12 }}>
							<TextField
								label="Title"
								variant="outlined"
								disabled
								value={Title}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 12 }}>
							<TextField
								label="Asset"
								variant="outlined"
								disabled
								value={description || ""}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 6, xs: 6 }}>
							<TextField
								label="Opened by"
								variant="outlined"
								disabled
								value={WorkOrderOpenedBy}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 6 }}>
							<TextField
								label="Opened"
								variant="outlined"
								disabled
								value={moment(OpenTimestamp).format("DD/MM/YYYY HH:mm A")}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>

						<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 3, xs: 12 }}>
							<TextField
								label="Original Title"
								variant="outlined"
								disabled
								value={TemplateTitle || " "}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 6 }}>
							<TextField
								label="Planned Hours:"
								variant="outlined"
								disabled
								value={WorkOrderPlannedHours}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 6 }}>
							<TextField
								label="Consumed Hours"
								variant="outlined"
								disabled
								value={ConsumedHours}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 sx={{ paddingRight: 0 }} size={{ md: 3, xs: 6 }}>
							<TextField
								label="Number of Workers"
								variant="outlined"
								disabled
								value={WorkOrderNumberOfWorkers}
								fullWidth
								margin="dense"
								autoComplete="off"
							/>
						</Grid2>
					</Grid2>
				</DialogContent>
				<DialogActions
					sx={{ padding: "0px 24px", marginTop: "24px", paddingBottom: "16px" }}
				>
					<Grid2 container spacing={2} justifyContent="space-between">
						<Grid2 size={{ md: 3, xs: 6 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleCancel}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 6 }}>
							<Button
								fullWidth
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								disabled={isSubmitLoading}
								variant="contained"
								color="primary"
								onClick={handleOpenWorkOrder}
							>
								Open Order
							</Button>
						</Grid2>
					</Grid2>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={openSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					Work order opened
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
